import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonService } from '@app/services/common.service';

@Component({
  selector: 'app-chat-media-preview',
  templateUrl: './chat-media-preview.component.html',
  styleUrls: ['./chat-media-preview.component.scss'],
})
export class ChatMediaPreviewComponent implements OnInit {
  currentCarouselIndex = 0;
  maxHeight: string = '80vh';
  maxWidth: string = '80vw';
  files: any[] = [];
  private urlCache: Map<any, SafeResourceUrl> = new Map();
  fileExtensionsDownloadable = ['doc', 'docx', 'csv'];

  getNormalizedFileType(file: any): string {
    const fileType = file?.fileType?.toLowerCase();
    if (fileType) return fileType;

    const mimeType = file?.mimeType?.toLowerCase() || '';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType === 'text/csv') return 'csv';
    if (
      mimeType === 'application/msword' ||
      mimeType ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return 'doc';
    }

    return file?.type?.toLowerCase() || '';
  }
  currentSafeUrl: SafeResourceUrl =
    this.sanitizer.bypassSecurityTrustResourceUrl('');

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { files: any[]; startIndex?: number },
    private dialogRef: MatDialogRef<ChatMediaPreviewComponent>,
    private sanitizer: DomSanitizer,
    private commonService: CommonService
  ) {
    this.files = data.files || [];
    // console.log("files ->",this.files);

    this.currentCarouselIndex = data.startIndex || 0;
    this.preloadUrls();
    this.updateCurrentUrl();
  }

  ngOnInit(): void {}

  private preloadUrls(): void {
    this.files.forEach((file) => {
      if (!this.urlCache.has(file)) {
        this.urlCache.set(file, this.sanitizeUrl(file));
      }
    });
  }

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  private updateCurrentUrl(): void {
    this.currentSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
    const currentFile = this.files[this.currentCarouselIndex];

    if (currentFile) {
      const fileType = this.getNormalizedFileType(currentFile);
      if (fileType === 'video') {
        setTimeout(() => {
          if (this.videoPlayer && this.videoPlayer.nativeElement) {
            const video = this.videoPlayer.nativeElement;
            video.pause();
            video.currentTime = 0;
            video.load();
          }
        });
        this.currentSafeUrl = this.getSafeUrl(currentFile);
      } else if (fileType === 'pdf') {
        this.currentSafeUrl = this.getSafeUrl(currentFile);
      }
    }
  }

  nextSlide() {
    if (this.currentCarouselIndex < this.files.length - 1) {
      this.currentCarouselIndex++;
      this.updateCurrentUrl();
    }
  }

  prevSlide() {
    if (this.currentCarouselIndex > 0) {
      this.currentCarouselIndex--;
      this.updateCurrentUrl();
    }
  }

  close() {
    this.dialogRef.close();
  }

  isImage(file: any): boolean {
    return this.getNormalizedFileType(file) === 'image';
  }

  canDownload(file: any): boolean {
    const fileType = this.getNormalizedFileType(file);
    return fileType === 'image' || fileType === 'video' || fileType === 'csv';
  }

  isCsv(file: any): boolean {
    return this.getNormalizedFileType(file) === 'csv';
  }

  isVideo(file: any): boolean {
    return this.getNormalizedFileType(file) === 'video';
  }

  isDocument(file: any): boolean {
    return this.getNormalizedFileType(file) === 'pdf';
  }

  isDownloadableFile(file: any): boolean {
    return this.fileExtensionsDownloadable.includes(
      this.getNormalizedFileType(file)
    );
  }

  isOtherFile(file: any): boolean {
    const fileType = file?.fileType?.toLowerCase();
    return fileType !== 'image' && fileType !== 'video' && fileType !== 'pdf';
  }

  downloadFile(fileItem: any) {
    const name = fileItem?.name || fileItem?.file?.name || 'file';

    if (fileItem?.downloadUrl) {
      this.commonService.downloadFromPresignedUrl(fileItem.downloadUrl, name);
      return;
    }

    this.commonService.downloadAttachment(
      fileItem?.file || fileItem?.previewUrl || fileItem?.url,
      name,
      fileItem?.mimeType
    );
  }

  getFileIcon(fileType: string): string {
    switch (fileType?.toLowerCase()) {
      case 'doc':
      case 'docx':
        return 'doc';
      case 'csv':
      case 'xls':
      case 'xlsx':
        return 'csv';
      default:
        return 'insert_drive_file';
    }
  }

  // getFileIconClass(fileType: string): string {
  //   switch (fileType?.toLowerCase()) {
  //     case 'doc':
  //     case 'docx':
  //       return 'icon-doc';
  //     case 'csv':
  //     case 'xls':
  //     case 'xlsx':
  //       return 'icon-csv';
  //     default:
  //       return 'icon-default';
  //   }
  // }

  get isLightBackground(): boolean {
    const currentFile = this.files[this.currentCarouselIndex];
    return !!currentFile && this.isDownloadableFile(currentFile);
  }

  getSafeUrl(file: any): SafeResourceUrl {
    if (!file) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('');
    }

    if (this.urlCache.has(file)) {
      return this.urlCache.get(file)!;
    }

    const sanitizedUrl = this.sanitizeUrl(file);
    this.urlCache.set(file, sanitizedUrl);
    return sanitizedUrl;
  }

  private sanitizeUrl(file: any): SafeResourceUrl {
    let previewUrl = file?.previewUrl;

    if (previewUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
    }

    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    if (typeof file === 'string') {
      return this.sanitizer.bypassSecurityTrustResourceUrl(file);
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  loadIframe() {
    // console.log('iframe loaded')
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.prevSlide();
    }

    if (event.key === 'ArrowRight') {
      this.nextSlide();
    }
  }
}
